// frontend/src/pages/BlogPost.js
// Single blog post page — loads post by slug, renders with SEO metadata.
// Falls back to 404-like state if slug not found.

import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Link as MuiLink,
  Button,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { getPostBySlug } from '../blog/posts';
import PostCTA from '../blog/PostCTA';

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 10 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            Post not found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            The article you're looking for doesn't exist or has moved.
          </Typography>
          <Button component={RouterLink} to="/blog" variant="outlined" startIcon={<BackIcon />}>
            Back to blog
          </Button>
        </Container>
      </Box>
    );
  }

  const canonicalUrl = `https://app.blackmount.ai/blog/${post.slug}`;
  const Body = post.render;

  // Schema.org Article JSON-LD — signals to Google this is a real article
  // with an author, publish date, and canonical URL. Codex recommended
  // Article + ItemList; ItemList is embedded in the comparison-table
  // structure below rather than duplicated in JSON-LD for now.
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: (post.authors || []).map(name => ({ '@type': 'Person', name })),
    publisher: {
      '@type': 'Organization',
      name: 'Blackmount',
      logo: {
        '@type': 'ImageObject',
        url: 'https://app.blackmount.ai/blackmount-logo.jpg',
      },
    },
    mainEntityOfPage: canonicalUrl,
    image: `https://app.blackmount.ai${post.ogImage || '/blackmount-logo.jpg'}`,
  };

  return (
    <>
      <Helmet>
        <title>{post.title} — Blackmount</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content={(post.tags || []).join(', ')} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`https://app.blackmount.ai${post.ogImage || '/blackmount-logo.jpg'}`} />
        <meta property="article:published_time" content={post.publishedAt} />
        {post.updatedAt && <meta property="article:modified_time" content={post.updatedAt} />}
        {(post.authors || []).map(a => <meta key={a} property="article:author" content={a} />)}
        {(post.tags || []).map(t => <meta key={t} property="article:tag" content={t} />)}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <meta name="twitter:image" content={`https://app.blackmount.ai${post.ogImage || '/blackmount-logo.jpg'}`} />

        <script type="application/ld+json">
          {JSON.stringify(articleJsonLd)}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<BackIcon />}
            size="small"
            sx={{ mb: 3, textTransform: 'none', color: 'text.secondary' }}
          >
            All posts
          </Button>

          <Typography variant="h3" component="h1" fontWeight={700} sx={{ mb: 2, lineHeight: 1.2 }}>
            {post.title}
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 5 }}>
            {(post.authors || []).map(a => (
              <Typography key={a} variant="body2" color="text.secondary">
                By {a}
              </Typography>
            ))}
            <Typography variant="body2" color="text.secondary">·</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(post.publishedAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">·</Typography>
            <Typography variant="body2" color="text.secondary">
              {post.readingTime} read
            </Typography>
          </Stack>

          <Box component="article">
            <Body />
          </Box>

          {/* Install CTA — shared across all posts (blog/PostCTA.jsx) */}
          <PostCTA />

          {/* Tag footer */}
          {post.tags?.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 6, mb: 4 }}>
              {post.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, fontSize: '0.75rem' }}
                />
              ))}
            </Stack>
          )}
        </Container>
      </Box>
    </>
  );
}
